import { useMemo } from 'react'
import { useQuery, useInfiniteQuery } from '@tanstack/react-query'
import type { InfiniteData } from '@tanstack/react-query'
import { cachePatch, listPatch, useOptimisticMutation } from '@/lib/optimistic-mutation'
import type { CachePatch } from '@/lib/optimistic-mutation'
import api from '@/lib/api'
import { cacheSync } from '@/lib/cacheSync'
import {
  NO_STUDENT_FILTERS,
  studentFilterQuery,
  type StudentFilters,
} from '@/lib/studentFilters'
import type {
  AcademicYear,
  Course,
  Term,
  TermCourse,
  Student,
  StudentRosterStatus,
  Enrollment,
  Teacher,
  Department,
  Parent,
  ParentStudentLink,
  ParentRelationship,
  Person,
  PersonRole,
  RoleGrant,
  CreatePersonRequest,
  RoleGrantRequest,
} from '@/types/api'
import { adminKeys } from '@/lib/queryKeys'
import { REFERENCE_DATA_STALE_TIME } from '@/lib/staleTimes'

/**
 * Re-exported from `lib/queryKeys`, which owns the whole key space — see the note
 * there. A query still names its key from the module it belongs to.
 */
export { adminKeys }

// ============ Academic Years Queries ============

/**
 * The years the school has set up. Read on every page that carries the topbar's
 * year menu, which is most of them, and added to once a year — so it takes the
 * reference-data freshness rather than the app-wide minute. Every write here
 * goes through `cacheSync.academicYears()`, which still refetches it at once.
 */
export function useAcademicYears() {
  return useQuery({
    queryKey: adminKeys.academicYears(),
    queryFn: async () => {
      const res = await api.get<{ academicYears: AcademicYear[] }>('/api/academic-years')
      return res.data.academicYears
    },
    staleTime: REFERENCE_DATA_STALE_TIME,
  })
}

export function useActiveAcademicYear() {
  return useQuery({
    queryKey: [...adminKeys.academicYears(), 'active'],
    queryFn: async () => {
      const res = await api.get<{ academicYear: AcademicYear }>('/api/academic-years/active')
      return res.data.academicYear
    },
    // Same list, same rate of change — and `cacheSync.academicYears()` names the
    // prefix, so activating a year reaches this entry too.
    staleTime: REFERENCE_DATA_STALE_TIME,
  })
}

export function useCreateAcademicYear() {
  return useOptimisticMutation({
    mutationFn: async (data: {
      name: string
      startDate: string
      endDate: string
      isActive?: boolean
    }) => {
      const res = await api.post<{ academicYear: AcademicYear }>('/api/academic-years', data)
      return res.data.academicYear
    },
    invalidateKeys: cacheSync.academicYears(),
  })
}

export function useUpdateAcademicYear() {
  return useOptimisticMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string
      data: Partial<{
        name: string
        startDate: string
        endDate: string
      }>
    }) => {
      const res = await api.put<{ academicYear: AcademicYear }>(`/api/academic-years/${id}`, data)
      return res.data.academicYear
    },
    // `exact` throughout this group: `adminKeys.academicYears()` is also a prefix
    // of the active-year query, whose cached value is a single AcademicYear
    // rather than a list. A prefix patch would hand a list updater an object.
    optimistic: (v) => [
      cachePatch<AcademicYear[]>(
        adminKeys.academicYears(),
        (years) => listPatch.update<AcademicYear>(years, v.id, v.data),
        { exact: true }
      ),
    ],
    invalidateKeys: cacheSync.academicYears(),
  })
}

export function useActivateAcademicYear() {
  return useOptimisticMutation({
    mutationFn: async (id: string) => {
      const res = await api.put<{ academicYear: AcademicYear }>(`/api/academic-years/${id}/activate`)
      return res.data.academicYear
    },
    // Exactly one year is active, so activating one deactivates the rest — the
    // patch has to move the flag off the others or the list shows two.
    optimistic: (id) => [
      cachePatch<AcademicYear[]>(
        adminKeys.academicYears(),
        (years) => years.map((year) => ({ ...year, isActive: year.id === id })),
        { exact: true }
      ),
    ],
    invalidateKeys: cacheSync.academicYears(),
  })
}

export function useArchiveAcademicYear() {
  return useOptimisticMutation({
    mutationFn: async (id: string) => {
      const res = await api.put<{ academicYear: AcademicYear }>(`/api/academic-years/${id}/archive`)
      return res.data.academicYear
    },
    optimistic: (id) => [
      cachePatch<AcademicYear[]>(
        adminKeys.academicYears(),
        (years) => listPatch.update<AcademicYear>(years, id, { isArchived: true }),
        { exact: true }
      ),
    ],
    invalidateKeys: cacheSync.academicYears(),
  })
}

export function useDeleteAcademicYear() {
  return useOptimisticMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api/academic-years/${id}`)
    },
    optimistic: (id) => [
      cachePatch<AcademicYear[]>(
        adminKeys.academicYears(),
        (years) => listPatch.remove(years, id),
        { exact: true }
      ),
    ],
    invalidateKeys: cacheSync.academicYears(),
  })
}

// ============ Shared Queries ============

export function useCourses() {
  return useQuery({
    queryKey: adminKeys.courses(),
    queryFn: async () => {
      const res = await api.get<{ courses: Course[] }>('/api/courses')
      return res.data.courses
    },
  })
}

export function useTerms(
  academicYearId?: string,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: academicYearId ? [...adminKeys.terms(), academicYearId] : adminKeys.terms(),
    queryFn: async () => {
      const url = academicYearId ? `/api/terms?academicYearId=${academicYearId}` : '/api/terms'
      const res = await api.get<{ terms: Term[] }>(url)
      return res.data.terms
    },
    // Callers that only want a specific year's terms can sit the query out
    // rather than falling through to the unfiltered `/api/terms` fetch.
    enabled: options?.enabled ?? true,
  })
}

export function useTermCourses() {
  return useQuery({
    queryKey: adminKeys.termCourses(),
    queryFn: async () => {
      const res = await api.get<{ termCourses: TermCourse[] }>('/api/term-courses')
      return res.data.termCourses
    },
  })
}

/** Matches DEFAULT_PAGE_SIZE on the API; the roster is served a page at a time. */
export const STUDENTS_PAGE_SIZE = 50

/** Matches MAX_PAGE_SIZE on the API - the fewest round trips a full roster takes. */
const STUDENTS_MAX_PAGE_SIZE = 100

interface StudentsPage {
  students: Student[]
  pagination?: {
    page: number
    limit: number
    total: number
    totalPages: number
  }
}

/**
 * Which homeroom the roster is narrowed to: an id, the literal `'none'` for the
 * children in no group, or null for the whole roster.
 *
 * @deprecated The homeroom is one axis of `StudentFilters` now — see
 * `lib/studentFilters`. Kept as an alias so nothing that already speaks this
 * type has to change at once.
 */
export type HomeroomFilter = string | 'none' | null

async function fetchStudentsPage(
  page: number,
  limit: number,
  status: StudentRosterStatus = 'active',
  filters: StudentFilters = NO_STUDENT_FILTERS
): Promise<StudentsPage> {
  // Left off entirely for the default so the roster request is byte-for-byte the
  // one it has always been; `status` is only ever sent when asking for the slice
  // the endpoint does not serve by default.
  const query = status === 'active' ? '' : `&status=${status}`
  // Likewise absent unless the roster is actually filtered — `studentFilterQuery`
  // is empty for an unfiltered one, and leaves off every axis nobody chose. An
  // empty `&gradeLevel=` would be an unfiltered roster wearing a filter's clothes.
  const res = await api.get<StudentsPage>(
    `/api/students?page=${page}&limit=${limit}${query}${studentFilterQuery(filters)}`
  )
  return res.data
}

/**
 * The values the roster's filters can take that the client cannot work out for
 * itself — currently just the graduation years its students are actually in.
 *
 * Not derived from the loaded rows: the roster is paged, so that would offer
 * only the cohorts that happen to fit on page one and hide the far-off ones a
 * filter is most useful for.
 */
export interface StudentFilterOptions {
  graduationYears: number[]
}

export function useStudentFilterOptions() {
  return useQuery({
    queryKey: [...adminKeys.students(), 'filter-options'] as const,
    queryFn: async (): Promise<StudentFilterOptions> => {
      const res = await api.get<Partial<StudentFilterOptions>>('/api/students/filter-options')
      // Defaulted rather than trusted: an older API answers this path from
      // `/:id` and hands back a student, and a filter picker with no options is
      // a better failure than a crashed page.
      return { graduationYears: res.data?.graduationYears ?? [] }
    },
    // A cohort appears when a student is enrolled into a new leaving year, which
    // is rare enough that refetching the list on every mount is pure noise.
    staleTime: REFERENCE_DATA_STALE_TIME,
  })
}

/**
 * Every page of the roster, concatenated.
 *
 * For the call sites that filter over the whole roster client-side - "students
 * not yet linked to this parent", "students eligible for this course" - where a
 * single page would silently narrow the choices rather than visibly truncate a
 * list. Prefer `useStudentsInfinite` for anything the user reads a page at a
 * time; this one is a full fetch.
 */
export async function fetchAllStudents(): Promise<Student[]> {
  const first = await fetchStudentsPage(1, STUDENTS_MAX_PAGE_SIZE)

  // No pagination block means an unpaged payload - what came back is all of it.
  const totalPages = first.pagination?.totalPages ?? 1
  if (totalPages <= 1) return first.students

  // The roster has a total order server-side, so the offsets line up and the
  // remaining pages can be fetched at once rather than one after another.
  const rest = await Promise.all(
    Array.from({ length: totalPages - 1 }, (_, i) =>
      fetchStudentsPage(i + 2, STUDENTS_MAX_PAGE_SIZE)
    )
  )

  return [first, ...rest].flatMap((page) => page.students)
}

/**
 * The whole roster as a *query*, so anything that needs it inside a queryFn of
 * its own can address it by key rather than call `fetchAllStudents` directly.
 *
 * A bare call is invisible to the cache: what it pages is stored under whichever
 * query happened to make it, and the entry this key holds is neither read nor
 * written. A session that had already loaded the roll paged the whole thing
 * again — every page of it — the next time some other query wanted it. Through
 * `queryClient.fetchQuery(allStudentsQuery())` the same fetch serves the cached
 * roster where there is a fresh one, dedupes against a read already in flight,
 * and otherwise stores what it fetched where `useAllStudents` will find it.
 *
 * Read it with `queryClient.fetchQuery`, not `ensureQueryData`: the latter
 * returns the entry's contents whether or not they are still good, so a roster
 * invalidated by a student write — and not refetched, because nothing was
 * observing it — would go on being served from inside another query's `queryFn`.
 */
export function allStudentsQuery() {
  return {
    // Under adminKeys.students() so the student mutations invalidate it too.
    queryKey: [...adminKeys.students(), 'all'] as const,
    queryFn: fetchAllStudents,
  }
}

export function useAllStudents({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    ...allStudentsQuery(),
    // A full roster is several requests in a large school. Callers that only
    // want it behind a panel — an enrollment picker, a parent-link dialog —
    // should say so rather than pay for it on every page load.
    enabled,
  })
}

/**
 * The roster a page at a time, for a list the user pages through. Use
 * `useAllStudents` where the whole roster has to be in hand at once.
 *
 * `status` picks the slice: 'active' is the roster the endpoint serves by
 * default, 'withdrawn' the students it derives out of it. The two are separate
 * cache entries, so opening the withdrawn view never disturbs the loaded pages
 * of the roster, and a withdrawal invalidates both.
 */
/**
 * Where `status` sits in the key `useStudentsInfinite` builds below —
 * `['admin', 'students', 'infinite', limit, status, filterKey]`. Named because
 * `placeholderData` has only the previous key to tell the two slices apart.
 */
const STATUS_IN_ROSTER_KEY = 4

export function useStudentsInfinite(
  limit: number = STUDENTS_PAGE_SIZE,
  status: StudentRosterStatus = 'active',
  filters: StudentFilters = NO_STUDENT_FILTERS
) {
  // Keyed under adminKeys.students() so the student mutations, which invalidate
  // that prefix, refetch the loaded pages here too. The filters are part of the
  // key: each narrowing is its own set of pages, so changing grade does not
  // append one cohort's children to another's already-loaded pages.
  //
  // The query string rather than the object, because the object is rebuilt on
  // every render — a fresh identity each time is a fresh cache entry each time,
  // and the roster would refetch itself forever.
  const filterKey = studentFilterQuery(filters)
  const query = useInfiniteQuery({
    queryKey: [...adminKeys.students(), 'infinite', limit, status, filterKey] as const,
    initialPageParam: 1,
    queryFn: ({ pageParam }) => fetchStudentsPage(pageParam, limit, status, filters),
    // Each narrowing being its own set of pages means every filter change is a
    // cache miss, and a cache miss on an infinite query is an empty table: the
    // list being read vanished, a spinner took its place, and the new list
    // appeared where the old one had been. On a filter bar built for clicking
    // through, that is a page that flashes empty on every click.
    //
    // The previous rows stay on screen instead, flagged `isPlaceholderData`
    // while the narrowed list loads. It changes nothing about which rows are
    // correct — the old list is shown as the old list, then replaced.
    //
    // Within one slice only, which is why this is not a bare `keepPreviousData`.
    // The roster and the withdrawn list are the same query over two slices, and
    // across that boundary the previous rows are not an older answer to the same
    // question — they are children who are still here, held under a heading that
    // says they have left. Students.tsx reads exactly that shape as the server
    // having ignored `status` and replaces the whole list with "this server does
    // not support listing withdrawn students", and every row it does draw offers
    // "Reinstate" on a child who never left. So the tabs lend each other nothing.
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[STATUS_IN_ROSTER_KEY] === status ? previous : undefined,
    getNextPageParam: (lastPage, allPages) => {
      const pagination = lastPage.pagination
      // No pagination block means a single unpaged payload - nothing to follow.
      if (!pagination) return undefined
      return pagination.page < pagination.totalPages ? allPages.length + 1 : undefined
    },
  })

  const students = useMemo(
    () => query.data?.pages.flatMap((p) => p.students) ?? [],
    [query.data]
  )

  return {
    ...query,
    students,
    // The server's count of the whole roster, not just what has been loaded.
    total: query.data?.pages[0]?.pagination?.total ?? students.length,
  }
}

export function useEnrollments() {
  return useQuery({
    queryKey: adminKeys.enrollments(),
    queryFn: async () => {
      const res = await api.get<{ enrollments: Enrollment[] }>('/api/enrollments')
      return res.data.enrollments
    },
  })
}

// ============ Teachers Queries ============

/**
 * `GET /api/teachers` is `requireAdmin`, but Courses — which needs the roster for
 * its teacher picker — is on the sidebar for teachers too, so this fired a
 * request that could only come back 403 for every teacher who opened that page.
 *
 * The caller passes the role test in rather than the hook reading the session,
 * matching `useDashboardStats`: the gate stays visible at the call site.
 */
export function useTeachers({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: adminKeys.teachers(),
    enabled,
    queryFn: async () => {
      const res = await api.get<{ teachers: Teacher[] }>('/api/teachers')
      return res.data.teachers
    },
  })
}

export function useDepartments() {
  return useQuery({
    queryKey: adminKeys.departments(),
    queryFn: async () => {
      const res = await api.get<{ departments: Department[] }>('/api/departments')
      return res.data.departments
    },
  })
}

export function useCreateTeacher() {
  return useOptimisticMutation({
    mutationFn: async (data: {
      firstName: string
      lastName: string
      // Optional: a staff record does not have to be an account.
      email?: string | null
      loginEnabled?: boolean
      employeeId: string
      departmentId?: string
    }) => {
      const res = await api.post<{ teacher: Teacher }>('/api/teachers', data)
      return res.data.teacher
    },
    invalidateKeys: cacheSync.teachers(),
  })
}

export function useUpdateTeacher() {
  return useOptimisticMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string
      data: Partial<{
        firstName: string
        lastName: string
        email: string | null
        // The sign-in grant. Absent from a request that is not about access;
        // present, it is what the API provisions (or revokes) against.
        loginEnabled: boolean
        employeeId: string
        departmentId: string | null
      }>
    }) => {
      const res = await api.put<{ teacher: Teacher }>(`/api/teachers/${id}`, data)
      return res.data.teacher
    },
    optimistic: (v) => [
      cachePatch<Teacher[]>(
        adminKeys.teachers(),
        (teachers) => listPatch.update<Teacher>(teachers, v.id, v.data)
      ),
    ],
    invalidateKeys: cacheSync.teachers(),
  })
}

export function useDeleteTeacher() {
  return useOptimisticMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api/teachers/${id}`)
    },
    optimistic: (id) => [
      cachePatch<Teacher[]>(adminKeys.teachers(), (teachers) =>
        listPatch.remove(teachers, id)
      ),
    ],
    invalidateKeys: cacheSync.teachers(),
  })
}

// ============ Departments Mutations ============

export function useCreateDepartment() {
  return useOptimisticMutation({
    mutationFn: async (data: { name: string; description?: string }) => {
      const res = await api.post<{ department: Department }>('/api/departments', data)
      return res.data.department
    },
    invalidateKeys: cacheSync.departments(),
  })
}

export function useUpdateDepartment() {
  return useOptimisticMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string
      data: { name?: string; description?: string | null }
    }) => {
      const res = await api.put<{ department: Department }>(`/api/departments/${id}`, data)
      return res.data.department
    },
    optimistic: (v) => [
      cachePatch<Department[]>(
        adminKeys.departments(),
        (departments) => listPatch.update<Department>(departments, v.id, v.data)
      ),
    ],
    invalidateKeys: cacheSync.departments(),
  })
}

export function useDeleteDepartment() {
  return useOptimisticMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api/departments/${id}`)
    },
    // Only the department row is patched. What a deletion does to the teachers
    // that referenced it is the server's decision, so that list waits for the
    // refetch rather than being guessed at here.
    optimistic: (id) => [
      cachePatch<Department[]>(adminKeys.departments(), (departments) =>
        listPatch.remove(departments, id)
      ),
    ],
    invalidateKeys: cacheSync.departments(),
  })
}

// ============ Parents Queries ============

export function useParents() {
  return useQuery({
    queryKey: adminKeys.parents(),
    queryFn: async () => {
      const res = await api.get<{ parents: Parent[] }>('/api/parents')
      return res.data.parents
    },
  })
}

export function useParentStudentLinks() {
  return useQuery({
    queryKey: adminKeys.parentStudentLinks(),
    queryFn: async () => {
      const res = await api.get<{ links: ParentStudentLink[] }>('/api/parent-students')
      return res.data.links
    },
  })
}

export function useCreateParent() {
  return useOptimisticMutation({
    mutationFn: async (data: {
      firstName: string
      lastName: string
      // Optional: the directory lists parents who never sign in.
      email?: string | null
      loginEnabled?: boolean
    }) => {
      const res = await api.post<{ parent: Parent }>('/api/parents', data)
      return res.data.parent
    },
    invalidateKeys: cacheSync.parents(),
  })
}

export function useUpdateParent() {
  return useOptimisticMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string
      data: {
        firstName: string
        lastName: string
        email: string | null
        // The sign-in grant - see `useUpdateTeacher`.
        loginEnabled?: boolean
      }
    }) => {
      const res = await api.put<{ parent: Parent }>(`/api/parents/${id}`, data)
      return res.data.parent
    },
    optimistic: (v) => [
      cachePatch<Parent[]>(
        adminKeys.parents(),
        (parents) => listPatch.update<Parent>(parents, v.id, v.data)
      ),
    ],
    invalidateKeys: cacheSync.parents(),
  })
}

export function useDeleteParent() {
  return useOptimisticMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api/parents/${id}`)
    },
    optimistic: (id) => [
      cachePatch<Parent[]>(adminKeys.parents(), (parents) =>
        listPatch.remove(parents, id)
      ),
    ],
    invalidateKeys: cacheSync.parents(),
  })
}

export function useLinkParentStudent() {
  return useOptimisticMutation({
    mutationFn: async (data: {
      parentId: string
      studentId: string
      relationship: ParentRelationship
      isPrimary: boolean
    }) => {
      const res = await api.post('/api/parent-students', data)
      return res.data
    },
    invalidateKeys: cacheSync.parentLinks(),
  })
}

export function useUnlinkParentStudent() {
  return useOptimisticMutation({
    mutationFn: async (linkId: string) => {
      await api.delete(`/api/parent-students/${linkId}`)
    },
    optimistic: (linkId) => [
      cachePatch<ParentStudentLink[]>(
        adminKeys.parentStudentLinks(),
        (links) => listPatch.remove(links, linkId)
      ),
    ],
    invalidateKeys: cacheSync.parentLinks(),
  })
}

// ============ Courses Page Mutations ============

export function useCreateCourse() {
  return useOptimisticMutation({
    mutationFn: async (data: {
      courseName: string
      courseCode: string
      level: 'HL' | 'SL' | 'Standard'
      // Optional on a course: null is "credits do not apply to this one".
      credits: number | null
      departmentId: string | null
      description?: string
      // Some subjects mix grade levels (e.g. Theatre combines 7 and 8); the deprecated
      // `gradeLevel` scalar is still accepted by the API for backward compatibility but
      // the frontend now always writes the array.
      gradeLevels?: number[]
    }) => {
      const res = await api.post<{ course: Course }>('/api/courses', data)
      return res.data.course
    },
    invalidateKeys: cacheSync.courses(),
  })
}

export function useUpdateCourse() {
  return useOptimisticMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string
      data: {
        courseName: string
        courseCode: string
        level: 'HL' | 'SL' | 'Standard'
        credits: number | null
        departmentId: string | null
        description?: string
        gradeLevels?: number[]
      }
    }) => {
      const res = await api.put<{ course: Course }>(`/api/courses/${id}`, data)
      return res.data.course
    },
    optimistic: (v) => [
      cachePatch<Course[]>(
        adminKeys.courses(),
        (courses) => listPatch.update<Course>(courses, v.id, v.data)
      ),
    ],
    invalidateKeys: cacheSync.courses(),
  })
}

export function useDeleteCourse() {
  return useOptimisticMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api/courses/${id}`)
    },
    optimistic: (id) => [
      cachePatch<Course[]>(adminKeys.courses(), (courses) =>
        listPatch.remove(courses, id)
      ),
    ],
    invalidateKeys: cacheSync.courses(),
  })
}

export function useCreateTerm() {
  return useOptimisticMutation({
    mutationFn: async (data: {
      termName: string
      academicYear: string
      academicYearId?: string
      startDate?: string
      endDate?: string
      gradeSubmissionDeadline?: string
    }) => {
      const res = await api.post<{ term: Term }>('/api/terms', data)
      return res.data.term
    },
    invalidateKeys: cacheSync.terms(),
  })
}

export function useUpdateTerm() {
  return useOptimisticMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string
      data: {
        termName: string
        academicYear: string
        academicYearId?: string
        startDate?: string
        endDate?: string
        gradeSubmissionDeadline?: string
      }
    }) => {
      const res = await api.put<{ term: Term }>(`/api/terms/${id}`, data)
      return res.data.term
    },
    // A prefix patch here, not an exact one: terms are cached per academic year
    // as well as unfiltered (`[...terms(), academicYearId]`), and every one of
    // those entries is a Term[]. Patching the prefix keeps them all in step.
    optimistic: (v) => [
      cachePatch<Term[]>(
        adminKeys.terms(),
        (terms) => listPatch.update<Term>(terms, v.id, v.data)
      ),
    ],
    invalidateKeys: cacheSync.terms(),
  })
}

export function useDeleteTerm() {
  return useOptimisticMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api/terms/${id}`)
    },
    optimistic: (id) => [
      cachePatch<Term[]>(adminKeys.terms(), (terms) => listPatch.remove(terms, id)),
    ],
    invalidateKeys: cacheSync.terms(),
  })
}

export function useLockTerm() {
  return useOptimisticMutation({
    mutationFn: async (id: string) => {
      await api.post(`/api/terms/${id}/lock`)
    },
    optimistic: (id) => [
      cachePatch<Term[]>(adminKeys.terms(), (terms) =>
        listPatch.update<Term>(terms, id, { status: 'locked' })
      ),
    ],
    invalidateKeys: cacheSync.terms(),
  })
}

export function useUnlockTerm() {
  return useOptimisticMutation({
    mutationFn: async (id: string) => {
      await api.post(`/api/terms/${id}/unlock`)
    },
    // `approved`, not `open`: the unlock endpoint rewinds a locked term to the
    // state it was locked from (see the terms route), so guessing `open` here
    // would show a status the term never had until the refetch corrected it.
    optimistic: (id) => [
      cachePatch<Term[]>(adminKeys.terms(), (terms) =>
        listPatch.update<Term>(terms, id, { status: 'approved' })
      ),
    ],
    invalidateKeys: cacheSync.terms(),
  })
}

export function useCreateTermCourse() {
  return useOptimisticMutation({
    mutationFn: async (data: { termId: string; courseId: string; teacherId?: string }) => {
      const res = await api.post<{ termCourse: TermCourse }>('/api/term-courses', data)
      return res.data.termCourse
    },
    invalidateKeys: cacheSync.termCourses(),
  })
}

export function useDeleteTermCourse() {
  return useOptimisticMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api/term-courses/${id}`)
    },
    optimistic: (id) => [
      cachePatch<TermCourse[]>(adminKeys.termCourses(), (termCourses) =>
        listPatch.remove(termCourses, id)
      ),
      // Its enrollments go with it, and they are keyed by term-course, not by
      // the id being deleted.
      cachePatch<Enrollment[]>(adminKeys.enrollments(), (enrollments) =>
        enrollments.filter((enrollment) => enrollment.termCourseId !== id)
      ),
    ],
    invalidateKeys: cacheSync.termCourses(),
  })
}

export function useSaveEnrollments() {
  return useOptimisticMutation({
    mutationFn: async ({
      termCourseId,
      currentEnrollments,
      selectedStudentIds,
    }: {
      termCourseId: string
      currentEnrollments: Enrollment[]
      selectedStudentIds: Set<string>
    }) => {
      // Only consider active enrollments as "current" - withdrawn/dropped students can be re-enrolled
      const activeEnrollments = currentEnrollments.filter((e) => e.status === 'active')
      const currentStudentIds = new Set(activeEnrollments.map((e) => e.studentId))

      const toEnroll = Array.from(selectedStudentIds).filter(
        (id) => !currentStudentIds.has(id)
      )
      const toUnenroll = activeEnrollments.filter(
        (e) => !selectedStudentIds.has(e.studentId)
      )

      await Promise.all([
        ...toEnroll.map((studentId) =>
          api.post('/api/enrollments', {
            studentId,
            termCourseId,
            status: 'active',
            enrollmentDate: new Date().toISOString(),
          })
        ),
        // Withdraw removed enrollments (soft delete - preserves grades)
        ...toUnenroll.map((e) => api.delete(`/api/enrollments/${e.id}`)),
      ])

      return { enrolled: toEnroll.length, unenrolled: toUnenroll.length }
    },
    // Withdrawals only: a new enrollment has no server id yet, and inventing one
    // would put a row on screen that the refetch then replaces with a different
    // one. Removals are exact, and are the change the admin is watching for.
    optimistic: (v) => [
      cachePatch<Enrollment[]>(adminKeys.enrollments(), (enrollments) =>
        enrollments.filter(
          (enrollment) =>
            enrollment.termCourseId !== v.termCourseId ||
            enrollment.status !== 'active' ||
            v.selectedStudentIds.has(enrollment.studentId)
        )
      ),
    ],
    invalidateKeys: (variables) =>
      cacheSync.enrollments({ termCourseId: variables.termCourseId }),
  })
}

/**
 * The roster is cached in two shapes under `adminKeys.students()` — a flat list
 * (`useAllStudents`) and paged `InfiniteData` (`useStudentsInfinite`) — so a
 * single prefix patch cannot serve both. These build the pair.
 */
function studentListPatches(edit: (students: Student[]) => Student[]): CachePatch[] {
  return [
    cachePatch<Student[]>([...adminKeys.students(), 'all'], edit, { exact: true }),
    cachePatch<InfiniteData<StudentsPage>>([...adminKeys.students(), 'infinite'], (data) => ({
      ...data,
      pages: data.pages.map((page) => ({ ...page, students: edit(page.students) })),
    })),
  ]
}

// ============ Student Mutations ============

export function useCreateStudent() {
  return useOptimisticMutation({
    mutationFn: async (data: {
      studentId: string
      firstName: string
      lastName: string
      email?: string | null
      loginEnabled?: boolean
      gradeLevel: number
      /** Null when the office does not know it yet - the column is nullable. */
      graduationYear: number | null
      programType: Student['programType']
      homeroomId?: string | null
      photoConsent?: boolean | null
    }) => {
      const res = await api.post<{ student: Student }>('/api/students', data)
      return res.data.student
    },
    invalidateKeys: (_variables, student) => cacheSync.students({ studentId: student?.id }),
  })
}

export function useUpdateStudent() {
  return useOptimisticMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string
      data: Partial<{
        firstName: string
        lastName: string
        email: string | null
        // The sign-in grant - see `useUpdateTeacher`.
        loginEnabled: boolean
        studentId: string
        gradeLevel: number
        graduationYear: number | null
        programType: Student['programType']
        homeroomId: string | null
        photoConsent: boolean | null
        cumulativeGpa: number | null
        credits: number | null
      }>
      /**
       * What the roster should *call* the new homeroom until it refetches.
       *
       * Not part of the write - the server denormalises `homeroomName` into the
       * roster row from `homeroomId`, and it is never accepted as input - so it
       * travels beside `data` rather than in it, and is never sent. Without it
       * the optimistic patch moves a child into a group whose name the row goes
       * on printing as the old one, which is the flicker `useOptimisticMutation`
       * exists to remove, only worse: the cell states a fact that is now false.
       *
       * Optional, and read by presence: a caller that says nothing about the
       * name leaves the cached one alone rather than blanking it.
       */
      homeroomName?: string | null
    }) => {
      const res = await api.put<{ student: Student }>(`/api/students/${id}`, data)
      return res.data.student
    },
    optimistic: (v) =>
      studentListPatches((students) =>
        students.map((student) =>
          student.id === v.id
            ? {
                ...student,
                ...v.data,
                // The form clears these to null; `Student` spells "no value" as
                // undefined. Keyed on presence, not on nullishness — reading a
                // `?? undefined` off an absent field would wipe a value the edit
                // never touched.
                cumulativeGpa:
                  'cumulativeGpa' in v.data
                    ? (v.data.cumulativeGpa ?? undefined)
                    : student.cumulativeGpa,
                credits: 'credits' in v.data ? (v.data.credits ?? undefined) : student.credits,
                homeroomName:
                  'homeroomName' in v ? v.homeroomName : student.homeroomName,
              }
            : student
        )
      ),
    // The homeroom list is part of `cacheSync.students` because a group's
    // headcount is one of the things this write moves. `Homeroom.studentCount`
    // is counted server-side and shown on the Homerooms page and in the
    // register's picker, so without it an admin who files a child from the
    // roster and clicks straight through sees the group they just filled still
    // reading its old number.
    invalidateKeys: (variables) => cacheSync.students({ studentId: variables.id }),
  })
}

export function useDeleteStudent() {
  return useOptimisticMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api/students/${id}`)
    },
    // The paged totals are left alone: the count is the server's, and correcting
    // it here would only race the refetch that is already on its way.
    optimistic: (id) => studentListPatches((students) => listPatch.remove(students, id)),
    invalidateKeys: (id) => cacheSync.students({ studentId: id }),
  })
}

// ============ People (the directory) ============

/**
 * Everyone at the school, with the offices each holds.
 *
 * The list `useTeachers`, `useStudents` and `useParents` could not produce
 * between them: they each show one kind of person, admins appear in none of
 * them, and a teacher who is also a parent appears twice with no sign the two
 * rows are the same human.
 */
export const PEOPLE_PAGE_SIZE = 50

interface PeoplePage {
  people: Person[]
  /**
   * Absent from an older API build, which answered the whole school in one
   * payload. Optional so a stale Worker degrades to a single page rather than a
   * crash - the same allowance `useStudentsInfinite` makes.
   */
  pagination?: { page: number; limit: number; total: number; totalPages: number }
}

/**
 * Everyone at the school, with the offices each holds, a page at a time.
 *
 * The list `useTeachers`, `useStudents` and `useParents` could not produce
 * between them: they each show one kind of person, admins appear in none of
 * them, and a teacher who is also a parent appears twice with no sign the two
 * rows are the same human.
 *
 * Paged since #206 - the endpoint used to answer with the whole school, which is
 * fine for a demo roster and a ~1,500-row payload for a real one. `role` and
 * `search` are part of the query key, so each filtered view keeps its own pages
 * and switching filter never appends one slice's rows to another's.
 *
 * The filters are applied by the *server*, across the whole school. That matters
 * more now than it did: `DataTable`'s own search is client-side over the rows
 * loaded so far, and a role filter that worked the same way would have meant
 * "the coordinators among the first fifty people alphabetically".
 */
export function usePeopleInfinite(
  filters: { role?: PersonRole; search?: string } = {},
  limit: number = PEOPLE_PAGE_SIZE
) {
  const query = useInfiniteQuery({
    queryKey: [...adminKeys.people(filters), 'infinite', limit] as const,
    initialPageParam: 1,
    queryFn: async ({ pageParam }): Promise<PeoplePage> => {
      const params = new URLSearchParams({ page: String(pageParam), limit: String(limit) })
      if (filters.role) params.set('role', filters.role)
      if (filters.search) params.set('search', filters.search)
      const res = await api.get<PeoplePage>(`/api/people?${params.toString()}`)
      return res.data
    },
    getNextPageParam: (lastPage, allPages) => {
      const pagination = lastPage.pagination
      // No pagination block means a single unpaged payload - nothing to follow.
      if (!pagination) return undefined
      return pagination.page < pagination.totalPages ? allPages.length + 1 : undefined
    },
  })

  const people = useMemo(
    () => query.data?.pages.flatMap((p) => p.people) ?? [],
    [query.data]
  )

  return {
    ...query,
    people,
    // The server's count of everyone matching the filter, not just what has been
    // loaded - so a header can say "50 of 380" rather than "50".
    total: query.data?.pages[0]?.pagination?.total ?? people.length,
  }
}

/**
 * The three directory writes.
 *
 * All three were `useMutation` with a fire-and-forget `invalidateQueries` in
 * `onSuccess` — the pattern `lib/optimistic-mutation.ts` was written to replace,
 * and it failed here in both of the ways that module describes: the sheet that
 * `await`s the save closed over a directory that had not been re-read yet, and
 * the only lists named were the ones this page happened to know about.
 *
 * A person write can land on any of the per-role lists: creating someone with
 * the teacher role puts them on the staff list, granting `student` puts them on
 * the roster — which the old set left out, so a new pupil added through the
 * directory was missing from the Students page until it was reloaded. Which list
 * it will be is not knowable from here, so `cacheSync.people()` names them all.
 */
export function useCreatePerson() {
  return useOptimisticMutation({
    mutationFn: async (data: CreatePersonRequest) => {
      const res = await api.post<{ person: Person }>('/api/people', data)
      return res.data.person
    },
    invalidateKeys: cacheSync.people(),
  })
}

export function useGrantRole() {
  return useOptimisticMutation({
    mutationFn: async ({ personId, ...grant }: { personId: string } & RoleGrantRequest) => {
      const res = await api.post<{ roles: RoleGrant[] }>(
        `/api/people/${personId}/roles`,
        grant
      )
      return res.data.roles
    },
    invalidateKeys: cacheSync.people(),
  })
}

export function useRevokeRole() {
  return useOptimisticMutation({
    mutationFn: async ({ personId, grantId }: { personId: string; grantId: string }) => {
      const res = await api.delete<{ roles: RoleGrant[] }>(
        `/api/people/${personId}/roles/${grantId}`
      )
      return res.data.roles
    },
    invalidateKeys: cacheSync.people(),
  })
}
